/**
 * <mc-shipping-urgency> — Dynamic shipping cutoff message
 *
 * Calculates the next shipping window based on Eastern Time cutoffs
 * and displays the cutoff time converted to the visitor's local timezone.
 *
 * Data attributes (all times in ET, 24-hour format):
 *   data-weekday-hour, data-weekday-minute  – Mon–Thu cutoff (default 14:00)
 *   data-friday-hour, data-friday-minute    – Friday cutoff  (default 9:30)
 *
 * On Saturday/Sunday shows Monday shipping message.
 */
class McShippingUrgency extends HTMLElement {
  /** @type {number|null} */
  #interval = null;

  connectedCallback() {
    this.#update();
    this.#interval = setInterval(() => this.#update(), 60_000);
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
    this.#interval = null;
  }

  #update() {
    const weekdayHour = parseInt(this.dataset.weekdayHour || '14', 10);
    const weekdayMinute = parseInt(this.dataset.weekdayMinute || '0', 10);
    const fridayHour = parseInt(this.dataset.fridayHour || '9', 10);
    const fridayMinute = parseInt(this.dataset.fridayMinute || '30', 10);

    const now = new Date();
    const et = this.#getETParts(now);

    // Saturday (6) or Sunday (0) — show Monday shipping message
    if (et.day === 0 || et.day === 6) {
      this.hidden = false;
      const daysUntilMonday = et.day === 0 ? 1 : 2;
      const cutoffDate = this.#buildCutoffDate(now, daysUntilMonday, weekdayHour, weekdayMinute);
      const localTime = this.#formatLocalTime(cutoffDate);
      const dayLabel = et.day === 0 ? 'tomorrow' : 'Monday';
      const el = this.querySelector('[data-message]');
      if (el) el.textContent = `Order by ${localTime} ${dayLabel}, ships Monday`;
      return;
    }

    this.hidden = false;

    const isFriday = et.day === 5;
    const cutoffH = isFriday ? fridayHour : weekdayHour;
    const cutoffM = isFriday ? fridayMinute : weekdayMinute;
    const nowMinutes = et.hour * 60 + et.minute;
    const cutoffMinutes = cutoffH * 60 + cutoffM;
    const beforeCutoff = nowMinutes < cutoffMinutes;

    let message = '';

    if (beforeCutoff) {
      const cutoffDate = this.#buildCutoffDate(now, 0, cutoffH, cutoffM);
      const localTime = this.#formatLocalTime(cutoffDate);
      message = `Order by ${localTime}, ships today`;
    } else if (isFriday) {
      // Friday after cutoff → Monday
      const cutoffDate = this.#buildCutoffDate(now, 3, weekdayHour, weekdayMinute);
      const localTime = this.#formatLocalTime(cutoffDate);
      message = `Order by ${localTime} Monday, ships Monday`;
    } else if (et.day === 4) {
      // Thursday after cutoff → Friday
      const cutoffDate = this.#buildCutoffDate(now, 1, fridayHour, fridayMinute);
      const localTime = this.#formatLocalTime(cutoffDate);
      message = `Order by ${localTime} tomorrow, ships tomorrow`;
    } else {
      // Mon–Wed after cutoff → next day
      const cutoffDate = this.#buildCutoffDate(now, 1, weekdayHour, weekdayMinute);
      const localTime = this.#formatLocalTime(cutoffDate);
      message = `Order by ${localTime} tomorrow, ships tomorrow`;
    }

    const el = this.querySelector('[data-message]');
    if (el) el.textContent = message;
  }

  /**
   * Get current hour, minute, and day-of-week in Eastern Time.
   * @param {Date} date
   * @returns {{ hour: number, minute: number, day: number }}
   */
  #getETParts(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      weekday: 'short',
    }).formatToParts(date);

    const hour = parseInt(parts.find((p) => p.type === 'hour').value, 10);
    const minute = parseInt(parts.find((p) => p.type === 'minute').value, 10);
    const weekday = parts.find((p) => p.type === 'weekday').value;

    const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return { hour, minute, day: dayMap[weekday] };
  }

  /**
   * Build a Date object representing a specific ET cutoff time.
   * Uses the toLocaleString round-trip technique to handle EST/EDT.
   *
   * @param {Date} now        – current Date
   * @param {number} daysAhead – 0 = today, 1 = tomorrow, 3 = Monday from Friday
   * @param {number} hour      – cutoff hour in ET (24h)
   * @param {number} minute    – cutoff minute in ET
   * @returns {Date}
   */
  #buildCutoffDate(now, daysAhead, hour, minute) {
    // Create a Date whose local-time components equal the ET components of `now`
    const etNow = new Date(
      now.toLocaleString('en-US', { timeZone: 'America/New_York' })
    );

    // Offset between local timezone and ET (in ms)
    const offset = etNow.getTime() - now.getTime();

    // Build the cutoff in "fake local" time (actually ET components)
    const cutoff = new Date(etNow);
    cutoff.setDate(cutoff.getDate() + daysAhead);
    cutoff.setHours(hour, minute, 0, 0);

    // Convert back to a real UTC timestamp
    return new Date(cutoff.getTime() - offset);
  }

  /**
   * Format a Date as a short local time string (e.g. "2:00 PM", "9:30 AM").
   * @param {Date} date
   * @returns {string}
   */
  #formatLocalTime(date) {
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}

customElements.define('mc-shipping-urgency', McShippingUrgency);
